import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage, API_URL } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Gift, Users, Copy, Share2, TrendingUp, Award, QrCode, 
  ChevronRight, Sparkles
} from 'lucide-react';

export const ReferralPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await axios.get(`${API_URL}/referrals/my-stats`);
      setReferralData(response.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      // Demo data
      setReferralData({
        code: null,
        referrals_count: 0,
        total_bonus: 0,
        max_uses: 100
      });
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    try {
      const response = await axios.post(`${API_URL}/referrals/generate`);
      setReferralData(prev => ({ ...prev, code: response.data.code }));
      toast.success(language === 'fr' ? 'Code généré !' : 'Code generated!');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const copyCode = () => {
    if (referralData?.code) {
      navigator.clipboard.writeText(referralData.code);
      toast.success(language === 'fr' ? 'Code copié !' : 'Code copied!');
    }
  };

  const shareCode = async () => {
    if (referralData?.code && navigator.share) {
      try {
        await navigator.share({
          title: 'JoSmart Uni',
          text: language === 'fr' 
            ? `Rejoins-moi sur JoSmart Uni ! Utilise mon code: ${referralData.code}`
            : `Join me on JoSmart Uni! Use my code: ${referralData.code}`,
          url: window.location.origin
        });
      } catch (error) {
        copyCode();
      }
    } else {
      copyCode();
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-32 bg-gray-200 rounded-lg" />
        <div className="grid grid-cols-3 gap-4">
          <div className="h-24 bg-gray-200 rounded-lg" />
          <div className="h-24 bg-gray-200 rounded-lg" />
          <div className="h-24 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="referral-page">
      {/* Header */}
      <div>
        <h1 className="heading-2">
          {language === 'fr' ? 'Programme de parrainage' : 'Referral Program'}
        </h1>
        <p className="text-gray-500 mt-1">
          {language === 'fr' 
            ? 'Invitez vos amis et gagnez des bonus'
            : 'Invite friends and earn bonuses'}
        </p>
      </div>

      {/* Main Card */}
      <Card className="bg-gradient-to-br from-[var(--tenant-primary)] to-blue-700 text-white border-0 overflow-hidden">
        <CardContent className="p-8 relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Gift className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  {language === 'fr' ? 'Votre code de parrainage' : 'Your Referral Code'}
                </h2>
                <p className="text-blue-200 text-sm">
                  {language === 'fr' ? 'Partagez-le avec vos camarades' : 'Share it with your classmates'}
                </p>
              </div>
            </div>

            {referralData?.code ? (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-200 mb-1">
                      {language === 'fr' ? 'Code' : 'Code'}
                    </p>
                    <p className="text-2xl font-mono font-bold tracking-wider">
                      {referralData.code}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="secondary" 
                      size="icon"
                      className="bg-white/20 hover:bg-white/30 border-0"
                      onClick={copyCode}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="secondary"
                      className="bg-white/20 hover:bg-white/30 border-0 gap-2"
                      onClick={shareCode}
                    >
                      <Share2 className="w-4 h-4" />
                      {language === 'fr' ? 'Partager' : 'Share'}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Button 
                onClick={generateCode}
                className="bg-white text-[var(--tenant-primary)] hover:bg-blue-50 mb-6"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Générer mon code' : 'Generate my code'}
              </Button>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold">{referralData?.referrals_count || 0}</p>
                <p className="text-sm text-blue-200">
                  {language === 'fr' ? 'Parrainages' : 'Referrals'}
                </p>
              </div>
              <div className="text-center border-x border-white/20">
                <p className="text-3xl font-bold">${referralData?.total_bonus || 0}</p>
                <p className="text-sm text-blue-200">
                  {language === 'fr' ? 'Bonus gagnés' : 'Bonus earned'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">$10</p>
                <p className="text-sm text-blue-200">
                  {language === 'fr' ? 'Par parrainage' : 'Per referral'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {language === 'fr' ? 'Comment ça marche ?' : 'How it works?'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Step 
              number={1}
              icon={QrCode}
              title={language === 'fr' ? 'Partagez votre code' : 'Share your code'}
              description={language === 'fr' 
                ? 'Envoyez votre code unique à vos amis'
                : 'Send your unique code to your friends'}
            />
            <Step 
              number={2}
              icon={Users}
              title={language === 'fr' ? 'Ils s\'inscrivent' : 'They sign up'}
              description={language === 'fr'
                ? 'Vos amis utilisent votre code lors de l\'inscription'
                : 'Your friends use your code when registering'}
            />
            <Step 
              number={3}
              icon={Award}
              title={language === 'fr' ? 'Gagnez des bonus' : 'Earn bonuses'}
              description={language === 'fr'
                ? 'Recevez $10 pour chaque inscription réussie'
                : 'Receive $10 for each successful signup'}
            />
          </div>
        </CardContent>
      </Card>

      {/* Progress to goal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            {language === 'fr' ? 'Progression vers l\'objectif' : 'Progress to goal'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>{referralData?.referrals_count || 0} / 10 {language === 'fr' ? 'parrainages' : 'referrals'}</span>
                <span className="text-[var(--tenant-primary)]">
                  {language === 'fr' ? 'Prochain palier: $50 bonus' : 'Next tier: $50 bonus'}
                </span>
              </div>
              <Progress value={((referralData?.referrals_count || 0) / 10) * 100} className="h-3" />
            </div>
            
            <div className="grid grid-cols-3 gap-4 mt-6">
              <TierCard 
                tier={1}
                required={5}
                bonus="$25"
                achieved={(referralData?.referrals_count || 0) >= 5}
                language={language}
              />
              <TierCard 
                tier={2}
                required={10}
                bonus="$50"
                achieved={(referralData?.referrals_count || 0) >= 10}
                language={language}
              />
              <TierCard 
                tier={3}
                required={25}
                bonus="$150"
                achieved={(referralData?.referrals_count || 0) >= 25}
                language={language}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Step = ({ number, icon: Icon, title, description }) => (
  <div className="text-center">
    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-3 relative">
      <Icon className="w-6 h-6 text-[var(--tenant-primary)]" />
      <span className="absolute -top-2 -right-2 w-6 h-6 bg-[var(--tenant-primary)] text-white text-xs rounded-full flex items-center justify-center font-bold">
        {number}
      </span>
    </div>
    <h4 className="font-medium mb-1">{title}</h4>
    <p className="text-sm text-gray-500">{description}</p>
  </div>
);

const TierCard = ({ tier, required, bonus, achieved, language }) => (
  <div className={`p-4 rounded-lg border-2 text-center transition-all ${
    achieved 
      ? 'border-green-500 bg-green-50' 
      : 'border-gray-200 bg-gray-50'
  }`}>
    <Badge className={achieved ? 'badge-success' : 'bg-gray-200 text-gray-600'}>
      {language === 'fr' ? 'Palier' : 'Tier'} {tier}
    </Badge>
    <p className="text-2xl font-bold mt-2">{bonus}</p>
    <p className="text-xs text-gray-500">
      {required} {language === 'fr' ? 'parrainages' : 'referrals'}
    </p>
    {achieved && (
      <Badge className="badge-success mt-2">
        {language === 'fr' ? 'Atteint !' : 'Achieved!'}
      </Badge>
    )}
  </div>
);

export default ReferralPage;
